/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, computed, inject, ViewEncapsulation } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe } from '@ngx-translate/core';
import { AppLanguageService } from '../shared/language.service';
import { APP_VERSION, CHANGELOG, ChangeItem, ChangelogEntry, ChangeType } from './app-version';

@Component({
  selector: 'app-changelog-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, ThaiDatePipe, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="aca-changelog">
      <div class="aca-changelog__hero">
        <div>
          <p class="aca-changelog__eyebrow">WORKSPACE</p>
          <h2 class="aca-changelog__title">{{ 'DMS.CHANGELOG.TITLE' | translate }}</h2>
          <p class="aca-changelog__sub">{{ 'DMS.CHANGELOG.CURRENT' | translate: { p0: current } }}</p>
        </div>
        <mat-icon>auto_awesome</mat-icon>
      </div>

      <div class="aca-changelog__body" mat-dialog-content>
        <div class="aca-changelog__release" *ngFor="let r of log">
          <div class="aca-changelog__rel-head">
            <span class="aca-changelog__badge">v{{ r.version }}</span>
            <span class="aca-changelog__date">{{ r.date | acaDate: 'd MMM y' }}</span>
          </div>
          <div class="aca-changelog__rel-title" *ngIf="titleOf(r)">{{ titleOf(r) }}</div>
          <ul class="aca-changelog__list">
            <li *ngFor="let c of r.changes" class="aca-changelog__item aca-changelog__item--{{ c.type }}">
              <mat-icon>{{ iconFor(c.type) }}</mat-icon>
              <span>{{ textOf(c) }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="aca-changelog__actions" mat-dialog-actions align="end">
        <button mat-flat-button color="primary" mat-dialog-close>{{ 'DMS.CHANGELOG.GOT_IT' | translate }}</button>
      </div>
    </div>
  `
})
export class ChangelogDialogComponent {
  private readonly language = inject(AppLanguageService);

  readonly current = APP_VERSION;
  readonly log = CHANGELOG;

  /** The changes are written in Thai and English; any other language reads the English. */
  readonly thai = computed(() => this.language.current().toLowerCase().startsWith('th'));

  titleOf(entry: ChangelogEntry): string {
    return (this.thai() ? entry.titleTh : entry.title) || entry.title || '';
  }

  textOf(change: ChangeItem): string {
    return this.thai() ? change.th : change.text;
  }

  iconFor(type: ChangeType): string {
    return type === 'feature' ? 'star' : type === 'fix' ? 'bug_report' : 'tune';
  }
}
